<script setup>
/* eslint-disable */
import { onMounted, onUnmounted, inject, watch, ref, nextTick, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useSocketStore } from '@/stores/socket';
import { usePreSportsStore } from '@/stores/pre_sports';
import { useLiveSportsStore } from '@/stores/live_sports';
import { useFixtureStore } from '@/stores/fixture';
import { useDatesStore } from '@/stores/dates';
import { useGeneralStore } from '@/stores/general';
import { useCompetitionsStore } from '@/stores/competitions';
import emitter from '@/helpers/emmiter';
import LoaderComponent from '@/components/Common/LoaderComponent.vue';
import { Splide, SplideSlide } from '@splidejs/vue-splide';
import '@splidejs/vue-splide/css';
import headerIcons from '@/assets/svgs/Header.json'
import JerseyIcon from '@/assets/svgs/jerseyIcon.vue';

import FixtureBookMarket from '@/components/Common/FixtureBookMarket.vue';
import StatisticButton from '@/components/LiveSports/StatisticButton.vue';
import BetBoosterComponent from '@/components/Common/BetBooster/BetBoosterComponent.vue';
import { useBetBoosterStore } from '@/stores/bet_booster';

import slugify from "slugify";
import { usePlatformConfigurationStore } from '@/stores/platform_configuration';

const markets_closed = ref(false);
const show_scroll_to_top_button = ref(false);
const header_icons = ref(headerIcons);

const router = useRouter();
const route = useRoute();

const socketStore = useSocketStore();
const preSportsStore = usePreSportsStore();
const liveSportsStore = useLiveSportsStore();
const fixtureStore = useFixtureStore();
const datesStore = useDatesStore();
const generalStore = useGeneralStore();
const competitionsStore = useCompetitionsStore();
const betBoosterStore = useBetBoosterStore();
const platformConfigurationStore = usePlatformConfigurationStore();

const splideRef = ref(null);
const direction = ref(document.dir);
const options = ref({
	direction: direction.value,
	pagination: false,
	// arrows: false,
	// focus: 'center',
	autoWidth: true,
	gap: 20,
	trimSpace: 'move'
});

const checkArrowsVisibility = () => {
	if (splideRef.value && splideRef.value.splide) {
		const slider = splideRef.value.splide;
		let totalWidth = 0;
		slider.Components.Elements.slides.forEach(slide => {
			const marginRight = parseFloat(window.getComputedStyle(slide).marginRight);
			totalWidth += slide.offsetWidth + marginRight;
		});

		const sliderWidth = slider.Components.Elements.track.clientWidth;

		// Update arrow visibility
		slider.options = {
			...slider.options,
			arrows: totalWidth > sliderWidth
		};

		slider.refresh();
	}
};

watch(() => fixtureStore.marketGroups, (newValue, oldValue) => {
	if (newValue.length > 0) {
		nextTick().then(() => {
			checkArrowsVisibility();
		});
	}
});

const changeMarketGroup = (market_group) => {
	fixtureStore.setSelectedMarketGroup(market_group);
}

const disclaimerKey = computed(() => {
	if (!fixtureStore.fixtureBook?.sport_id || !fixtureStore.selectedMarketGroup) return '';
	const sportName = preSportsStore.preSportName(fixtureStore.fixtureBook.sport_id);
	if (!sportName) return '';
	return `market_groups.disclaimers.${slugify(sportName, { lower: true, strict: true })}.${slugify(fixtureStore.selectedMarketGroup, { lower: true, strict: true })}`;
});

const setPageTitle = () => {
	if (liveSportsStore.hasOneParticipant(fixtureStore.fixture.sport_id) || fixtureStore.fixture.outright == true) {
		document.title = generalStore.getGeneral.site_name + ' - ' + fixtureStore.fixture.participants[0];
		return;
	}

	document.title = generalStore.getGeneral.site_name + ' - ' + fixtureStore.fixture.participants[0] + ' - ' + fixtureStore.fixture.participants[1];
};

function handleSnapshot(data) {
	setPageTitle();
	competitionsStore.setSelectedCompetitionId(data.competition_id);
}

function handleNotFound() {
	router.push({ name: 'not-found' });
}

function handleDelete() {
	router.back();
}

onMounted(() => {
	emitter.on('liveFixtureBook', handleSnapshot);
	emitter.on('liveFixtureBookNotFound', handleNotFound);
	emitter.on('liveFixtureBookDelete', handleDelete);

	window.scrollTo(0, 0);

	if (!betBoosterStore.bet_boosted_fixtures.length && !betBoosterStore.loading) {
		betBoosterStore.fetchBoostedBets();
	}

	if (router.currentRoute.value.params.fixture_id) {
		fixtureStore.setFixtureLoading(true);
		socketStore.join('liveFixtureBook', router.currentRoute.value.params.fixture_id);
	}

	nextTick().then(() => {
		checkArrowsVisibility();
	});
	window.addEventListener('resize', checkArrowsVisibility);

	window.addEventListener('scroll', handleScroll);
	const container = document.querySelector('.live-tab-info');

	if (container) {
		container.addEventListener('scroll', handleElementScroll);
	}

})

const scrollToTop = () => {
	const container = document.querySelector('.live-tab-info');
	window.scrollTo({ top: 0 });

	if (container) {
		container.scrollTo({ top: 0 });
	}
};

const handleScroll = () => {
	const scrollTop = window.scrollY;
	show_scroll_to_top_button.value = scrollTop > 100;
};

const handleElementScroll = () => {
	const container = document.querySelector('.live-tab-info');

	if (container) {
		const scrollTop = container.scrollTop;
		show_scroll_to_top_button.value = scrollTop > 100;
	}
};

onUnmounted(() => {
	socketStore.leave('liveFixtureBook', fixtureStore.fixtureBook.id);
	fixtureStore.emptyFixture();

	window.removeEventListener('resize', checkArrowsVisibility);

	emitter.off('liveFixtureBook', handleSnapshot);
	emitter.off('liveFixtureBookNotFound', handleNotFound);
	emitter.off('liveFixtureBookDelete', handleDelete);
})

watch(() => fixtureStore.fixtureBook, (newFixturebook, oldFixtureBook) => {
	if (newFixturebook.live == true && oldFixtureBook.live == false) {
		router.push({ name: 'live-sports-eventview', params: { fixture_id: newFixturebook.id } })
	}

	if (newFixturebook.id != oldFixtureBook.id) checkImageExists();
})

watch(route, (to, from) => {
	if (!to.params.hasOwnProperty('fixture_id')) return;

	if (fixtureStore.fixtureBook.id == to.params.fixture_id) return;

	socketStore.leave('liveFixtureBook', fixtureStore.fixtureBook.id);
	fixtureStore.emptyFixture();

	fixtureStore.setFixtureLoading(true);
	socketStore.join('liveFixtureBook', to.params.fixture_id);
});

const goToCompetition = (fixture) => {
	if (usePlatformConfigurationStore().isNewPreSportsLayoutEnabled) {
		router.push({ name: 'pre-sports-coupons-competitions-from-to', params: { sport_id: fixture.sport_id, competition_ids: fixture.competition_id, from: 'all' } } )
		return;
	}

	router.push({ name: 'pre-sports-competition', params: { sport_id: fixture.sport_id, region_id: Number(fixture.region_id), competition_id: fixture.competition_id } } )
}

const checkImageExists = () => {
	let imageUrl = '/assets/scoreboards/' + preSportsStore.preSportName(fixtureStore.fixtureBook.sport_id) + '.jpg'

	// Create a new image object
	const img = new Image();

	// When image loads successfully, update the src
	img.onload = function () {
		document.getElementById('scoreboardImage').style = 'display:block;'
		document.getElementById('scoreboardImage').src = imageUrl;
	};

	// If image doesn't load, fall back to a default image
	img.onerror = function () {
		document.getElementById('scoreboardImage').style = 'display:none;'
	};

	// Try to load the generated image URL
	img.src = imageUrl;
}

</script>
<template>
	<LoaderComponent v-if="fixtureStore.fixture_loading" />
	<div class="live-sports-center" v-else>
		<div class="competetion-back-btn" :style="usePlatformConfigurationStore().isNewPreSportsLayoutEnabled ? 'padding-top: 15px;' : ''">
			<a @click="router.back()" class="">
				<svg xmlns="http://www.w3.org/2000/svg" width="4.414" height="7.414" viewBox="0 0 4.414 7.414">
					<g id="Group_232" data-name="Group 232" transform="translate(0.707 0.525)">
						<line id="Line_7" data-name="Line 7" x1="3" y2="3" transform="translate(0 0.182)" fill="none"
							stroke="var(--white)" stroke-linecap="round" stroke-width="1"></line>
						<line id="Line_8" data-name="Line 8" x1="3" y1="3" transform="translate(0 3.182)" fill="none"
							stroke="var(--white)" stroke-linecap="round" stroke-width="1">
						</line>
					</g>
				</svg>&nbsp; {{ $t('general.back') }}
			</a>
		</div>
		<div class="eventview-game-banner upcoming-scorecard">
			<div class="eventview-banner-image">
				<img id="scoreboardImage" alt="">
			</div>
			<div class="live-game-outer">
				<div class="live_score_Box">
					<div class="league-title" style="cursor:pointer;">
						<div class="title">
							<svg  class="normal-img"
								xmlns="http://www.w3.org/2000/svg"
								xml:space="preserve"
								version="1.1"
								style="width:23px; fill: none; stroke: var(--white); shape-rendering:geometricPrecision; text-rendering:geometricPrecision; image-rendering:optimizeQuality; fill-rule:evenodd; clip-rule:evenodd"
								viewBox="0 0 30 25"
								xmlns:xlink="http://www.w3.org/1999/xlink">
								<use :xlink:href="preSportsStore.preSportWhiteIcon(fixtureStore.fixtureBook.sport_id) + '#svg3108'" stroke-width="1px"  />
							</svg>
							<!-- <img :src="preSportsStore.preSportWhiteIcon(fixtureStore.fixtureBook.sport_id)" alt=""> -->
							<h6 @click="goToCompetition(fixtureStore.fixtureBook)">{{ fixtureStore.fixtureBook.region_name + ' - ' + fixtureStore.fixtureBook.competition_name
							}}</h6>
							<StatisticButton :stats_id="fixtureStore.fixtureBook.stats_id" />
						</div>
						<div class="upcoming-match-date">
							<p>{{ datesStore.preFixtureBookDate(fixtureStore.fixtureBook.start_datetime) }}</p>
							<p>{{ datesStore.preFixtureBookTime(fixtureStore.fixtureBook.start_datetime) }}</p>
						</div>
					</div>
					<!-- score panel start-->
					<div class="league-score">

						<div class="eventview-playing-info" :class="liveSportsStore.hasOneParticipant(fixtureStore.fixtureBook.sport_id) || fixtureStore.fixtureBook.outright ? 'single-participant' : ''">
							<div class="playing-team-inner">
								<div class="playing-team">
									<img
										v-if="fixtureStore.getParticipantLogo(fixtureStore.fixtureBook.participant_logos)"
										class="playing-team-logo"
										:src="fixtureStore.getParticipantLogo(fixtureStore.fixtureBook.participant_logos).home" alt="#">
									<JerseyIcon v-if="fixtureStore.getParticipantJersey(fixtureStore.fixtureBook.jersey_colors)" :jerseyColor="fixtureStore.getParticipantJersey(fixtureStore.fixtureBook.jersey_colors).home" :size="32" />
									<h6 v-if="fixtureStore.fixtureBook.participants">{{
										fixtureStore.fixtureBook.participants[0] }}</h6>
								</div>
								<div class="playing-score">
									<span>{{ datesStore.preFixtureBookDate(fixtureStore.fixtureBook.start_datetime)
									}}</span>
								</div>
							</div>
							<div class="divider"> - </div>
							<div class="playing-team-inner">
								<div class="playing-team"
									v-if="!liveSportsStore.hasOneParticipant(fixtureStore.fixtureBook.sport_id) && !fixtureStore.fixtureBook.outright">
									<h6 v-if="fixtureStore.fixtureBook.participants">{{
										fixtureStore.fixtureBook.participants[1] }}</h6>
									<img
										v-if="fixtureStore.getParticipantLogo(fixtureStore.fixtureBook.participant_logos)"
										class="playing-team-logo"
										:src="fixtureStore.getParticipantLogo(fixtureStore.fixtureBook.participant_logos).away" alt="#">
									<JerseyIcon v-if="fixtureStore.getParticipantJersey(fixtureStore.fixtureBook.jersey_colors)" :jerseyColor="fixtureStore.getParticipantJersey(fixtureStore.fixtureBook.jersey_colors).away" />
								</div>
								<div class="playing-score">
									<span>{{ datesStore.preFixtureBookTime(fixtureStore.fixtureBook.start_datetime)
									}}</span>
								</div>
							</div>
						</div>

					</div>
					<!-- score panel end-->
				</div>

			</div>
		</div>

		<!-- Bet Boost Component for this fixture -->
		<BetBoosterComponent v-if="platformConfigurationStore.isBetBoostFixtureWidgetEnabled" :fixture_id="router.currentRoute.value.params.fixture_id || fixtureStore.fixtureBook?.id" :selected_sport_id="fixtureStore.fixtureBook?.sport_id" :showTitle="false" />

		<!----- info tab start ------>
		<div class="live-bet-dashboard">
			<div class="top-search-bar">
				<div></div>
				<div class="header-search-field">
					<div class="header-search-inner">
						<input type="search" :placeholder="$t('general.search_for_specific_markets')" v-model="fixtureStore.market_search">
						<button type="submit">
							<div>
								<object v-html="header_icons.search"></object>
							</div>
						</button>
					</div>
				</div>
			</div>

			<div class="all_detail">
				<div class="slider-grid">
					<Splide ref="splideRef" :options="options" direction:direction>
						<SplideSlide v-for="(market_group) in fixtureStore.sortedMarketGroups(fixtureStore.marketGroups)" :key="market_group">
							<button class="tablinks" :class="{ 'active': market_group == fixtureStore.selectedMarketGroup }"
								@click="changeMarketGroup(market_group)">{{ $t("market_groups." + slugify(market_group, { lower: true, strict: true })) }} ({{fixtureStore.marketGroups[market_group]}})</button>
						</SplideSlide>
					</Splide>
				</div>
				<span class="toggle-markets">
					<div @click="markets_closed = !markets_closed" class="markets-toggle-fill">
						<svg v-if="!markets_closed" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" svg-inline=""
							title="Toggle" role="presentation" focusable="false" xmlns:xlink="http://www.w3.org/1999/xlink"
							class="markets-toggle-svg">
							<use xlink:href="/assets/icons/markets_close.svg#markets_close"></use>
						</svg>
						<svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" svg-inline="" title="Toggle"
							role="presentation" focusable="false" xmlns:xlink="http://www.w3.org/1999/xlink"
							class="markets-toggle-svg">
							<use xlink:href="/assets/icons/markets_open.svg#markets_open"></use>
						</svg>
					</div>
				</span>
			</div>

			<div class="live-tab-info pre-sports-fixture-book-scroll"
				v-if="fixtureStore.fixtureBook && fixtureStore.fixtureBook.markets && fixtureStore.fixtureBook.markets.length > 0">
				<div class="market-group-disclaimer" v-if="disclaimerKey && $te(disclaimerKey)">
					<i class="fas fa-info-circle" style="margin-right: 8px; height: 1.3em !important;"></i>
					<span>{{ $t(disclaimerKey) }}</span>
				</div>
				<div id="all_event" class="eventview-center-panel">
					<div class="live-info-outer" style="display:unset;">
						<FixtureBookMarket v-for="(market, index) in fixtureStore.fixtureBook.markets" :key="market.id"
							:market="market" :markets_closed="markets_closed"
							:index="index"
							/>
					</div>
				</div>

			</div>
			<div class="live-tab-info" v-else>
				<p class="no-bet-message"> {{ $t('general.no_markets') }}</p>
			</div>
			<!----- info tab start ------>

		</div>
		<!-- center grid end -->
	</div>
	<button @click="scrollToTop" type="button" class="btn scroll-to-top" id="btn-back-to-top" v-show="show_scroll_to_top_button">
		<i class="fas fa-arrow-up"></i>
	</button>
</template>
<style scoped>
@media screen and (min-width: 992px) and (max-width: 3000px) {
	button#btn-back-to-top {
		display:none;
	}
}
@media screen and (min-width: 320px) and (max-width: 991px) {
	button#btn-back-to-top {
		bottom: 95px;
		left: 20px;
	}
}
@media screen and (min-width: 320px) and (max-width: 767px) {
	svg.svg-inline--fa.fa-arrow-up.fa-w-14 {
		margin-bottom: 7px!important;
	}
	button#btn-back-to-top {
		width: 30px!important;
		height: 30px!important;
		font-size: 14px!important;
	}

	.single-participant {
		display: flex !important;
		/* grid-template-columns: 100% !important; */
		justify-content: center;
	}

	.single-participant .divider {
		display: none !important;
	}

}

.single-participant .playing-team-inner .playing-team h6 {
	text-overflow: unset !important;
	white-space: normal !important;
}

button#btn-back-to-top {
	background: var(--primary);
	width: 38px;
	height: 38px;
	border: none;
	border-radius: 50%;
	position: fixed;
	z-index: 99;
	box-shadow: 0 2px 4px var(--list-gray);
	font-size: 16px;
	cursor: pointer;
}
html[dir="rtl"] button#btn-back-to-top {
	left: unset;
	right:20px;
}
@media screen and (min-width: 320px) and (max-width: 991px) {
	button#btn-back-to-top {
		left: 10px;
	}
}
@media screen and (min-width: 320px) and (max-width: 767px) {
	html[dir="rtl"] button#btn-back-to-top {
		left: unset;
		right:10px;
	}
}
.all_detail {
	margin-bottom: 22px;
}

.slider-grid :deep(.splide__arrow:disabled) {
	display: none;
}
html[dir="rtl"] .slider-grid :deep(.splide__arrows .splide__arrow--next:after ) {
	rotate: 180deg;
}

html[dir="rtl"] .slider-grid :deep(.splide__arrows .splide__arrow--next) {
	left: 0px !important;
}

html[dir="rtl"] .slider-grid :deep(.splide__arrows .splide__arrow--prev:after ) {
	rotate: 180deg;
}
.slider-grid {
	display: grid;
	grid-auto-columns: minmax(142px, auto);
	grid-gap: 15px;
	grid-auto-flow: column;
	padding: 0px;
	width: 100%;
	height: 100%;
	transform: translateX(0px);
	transition: all 1s ease;
	border-bottom: 1px solid var(--border-light);
}

.slider-grid .tablinks {
	color: var(--light-gray);
	font-size: 13px;
	padding-bottom: 6px;
	text-transform: capitalize;
	cursor: pointer;
}

.slider-grid .active {
	color: var(--white);
	position: relative;
}

.all_detail {
	position: relative;
	display: grid;
	grid-template-columns: 97% 3%;
	overflow: hidden;
}

.tab {
	overflow-y: hidden;
	overflow-x: overlay;
}


::-webkit-scrollbar {
	width: 0px;
	height: 0px;
}

.all_detail::-webkit-scrollbar-thumb {
	background-color: var(--primary);
	border-radius: 2px;
	border: 3px solid linear-gradient(var(--primary) 0%, var(--primary) 100%);
}

.tablinks {
	white-space: nowrap;
}

@media screen and (min-width: 320px) and (max-width: 767px) {
	.all_detail {
		grid-template-columns: 93% 5%;
	}
}

.live-info-outer {
	gap: 0px !important;
}

html[dir="rtl"] .top-search-bar .header-search-field button {
	left: 14px;
	right: unset;
}


.market-group-disclaimer {
	background-color: var(--bet-bg);
	border: 1px solid var(--border-light);
	border-radius: 8px;
	padding: 12px 16px;
	margin-bottom: 16px;
	font-size: 13px;
	color: var(--white, #a8a8a8);
	line-height: 1.4;
	display: flex;
	align-items: flex-start;
}

.market-group-disclaimer i {
	flex-shrink: 0;
	margin-top: 3px;
}

@media screen and (min-width: 320px) and (max-width: 991px) {
	.sports-main .sports-center-content .eventview-center-panel .live-info-outer {
		border: none;
	}
}

.sports-main .sports-center-content .eventview-game-banner.upcoming-scorecard .live-game-outer .live_score_Box .league-title .title {
	grid-template-columns: auto 1fr auto;
}

.playing-team-logo{
	width: 2em;
	height: 2em;
	margin: 0 0.5rem;
}
</style>

<!-- Non-scoped: ::-webkit-scrollbar pseudo-elements don't work with Vue scoped styles -->
<style>
/* Custom scrollbar for PreSportsFixtureBook markets */
.live-tab-info.pre-sports-fixture-book-scroll {
	max-height: calc(100vh - 180px) !important;
	overflow-y: auto !important;
	overflow-x: hidden !important;
	scrollbar-width: thin !important;
	scrollbar-color: var(--primary) transparent !important;
}

.live-tab-info.pre-sports-fixture-book-scroll::-webkit-scrollbar {
	width: 5px !important;
}

.live-tab-info.pre-sports-fixture-book-scroll::-webkit-scrollbar-track {
	background: transparent !important;
	border-radius: 10px !important;
}

.live-tab-info.pre-sports-fixture-book-scroll::-webkit-scrollbar-thumb {
	background: var(--primary) !important;
	border-radius: 10px !important;
}

.live-tab-info.pre-sports-fixture-book-scroll::-webkit-scrollbar-thumb:hover {
	background: color-mix(in srgb, var(--primary) 80%, white) !important;
}

.live-tab-info.pre-sports-fixture-book-scroll::-webkit-scrollbar-button {
	display: none !important;
	width: 0 !important;
	height: 0 !important;
}
</style>